import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { OverviewSummary, Tool, ToolComparisonPoint } from '@ace-tracker/shared';
import { chartAxisTickProps, chartTooltipProps } from '../chartTheme';
import { TOOL_COLORS } from '../toolColors';
import { Button } from './Button';
import { ChartCard } from './ChartCard';

const TOOL_LABELS: Record<Tool, string> = { copilot: 'GitHub Copilot', 'claude-code': 'Claude Code', codex: 'OpenAI Codex', kiro: 'AWS Kiro', gemini: 'Gemini CLI' };

/**
 * The "All tools" comparison body — the one place this dashboard charts
 * Copilot, Claude Code, and Codex side by side. Deliberately limited to
 * normalized measures (active developers, requests, active minutes,
 * indicative cost); see dashboard/AGENTS.md "API rules" and
 * adoptionOverview.ts's header for why raw token counts are never compared
 * here — the three tools account for tokens differently.
 */
export function OverviewDashboardBody({
  summary,
  formatCurrency,
  SectionHeading,
  onSelectTool,
}: {
  summary: OverviewSummary;
  formatCurrency: (value: number, currency: string) => string;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
  onSelectTool: (tool: Tool) => void;
}) {
  const byTool = summary.byTool;
  const chartData = byTool.map((p) => ({ ...p, label: TOOL_LABELS[p.tool] }));
  const totalActiveDevelopers = byTool.reduce((sum, p) => sum + p.activeDevelopers, 0);
  const totalRequests = byTool.reduce((sum, p) => sum + p.requests, 0);

  return (
    <>
      <section>
        <SectionHeading
          title="At a glance"
          hint="Only measures that mean the same thing across all three tools are compared here"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {byTool.map((point) => (
            <ToolSummaryCard key={point.tool} point={point} formatCurrency={formatCurrency} onSelect={() => onSelectTool(point.tool)} />
          ))}
        </div>
      </section>

      <section>
        <SectionHeading title="Side-by-side comparison" hint={`${totalActiveDevelopers} active developers · ${totalRequests.toLocaleString()} requests across all tools`} />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <ComparisonChart title="Active developers" caption="Distinct developers with usage in range" dataKey="activeDevelopers" data={chartData} />
          <ComparisonChart title="Requests" caption="Cumulative counters, corrected max() per device per day" dataKey="requests" data={chartData} />
          <ComparisonChart title="Active minutes" caption="Summed per-session active time" dataKey="activeMinutes" data={chartData} />
          <ComparisonChart
            title="Indicative token cost"
            caption="Per-token pricing for Claude Code, Codex, and Gemini CLI. Copilot and Kiro are not included: their billing is not reconciled"
            dataKey="indicativeCost"
            data={chartData}
            valueFormatter={(value, point) => formatCurrency(value, point.currency)}
          />
        </div>
        <p className="mt-3 text-xs text-text-faint">
          Indicative token cost prices Claude Code, Codex, and Gemini CLI usage at the rates in Settings — see each tool's own tab for
          the basis and caveats (Gemini models need rates added in Settings; the price feed has none). GitHub Copilot is not included: GitHub bills Copilot in AI credits with an included
          allowance, and this dashboard has no billing feed for it yet, so no Copilot figure is shown as cost here (its
          legacy AIU estimate is on the Copilot tab). AWS Kiro is not included either: it bills in plan credits and records
          no token counts (its tab shows character-based estimates). Raw token counts are intentionally not shown: Claude Code's
          separate cache-read accounting and Codex's cache-inclusive input-token accounting make raw token totals
          misleading to compare directly.
        </p>
      </section>
    </>
  );
}

function ToolSummaryCard({
  point,
  formatCurrency,
  onSelect,
}: {
  point: ToolComparisonPoint;
  formatCurrency: (value: number, currency: string) => string;
  onSelect: () => void;
}) {
  return (
    <div className="relative rounded-card border border-border bg-surface p-5 shadow-sm">
      <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card" style={{ backgroundColor: TOOL_COLORS[point.tool] }} />
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-default">{TOOL_LABELS[point.tool]}</h3>
        <Button variant="text" className="!min-h-0 !p-0 text-xs" onClick={onSelect}>
          View details
        </Button>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
        <Metric label="Active developers" value={point.activeDevelopers.toLocaleString()} />
        <Metric label="Requests" value={point.requests.toLocaleString()} />
        <Metric label="Active minutes" value={point.activeMinutes.toLocaleString()} />
        <Metric
          label="Indicative token cost"
          value={point.indicativeCost === null ? 'Not included' : formatCurrency(point.indicativeCost, point.currency)}
        />
      </dl>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-text-faint">{label}</dt>
      <dd className="font-semibold tabular-nums text-text-default">{value}</dd>
    </div>
  );
}

function ComparisonChart({
  title,
  caption,
  dataKey,
  data,
  valueFormatter,
}: {
  title: string;
  caption: string;
  dataKey: keyof ToolComparisonPoint;
  data: (ToolComparisonPoint & { label: string })[];
  valueFormatter?: (value: number, point: ToolComparisonPoint) => string;
}) {
  return (
    <ChartCard title={title} caption={caption} height="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
          <XAxis type="number" {...chartAxisTickProps} />
          <YAxis type="category" dataKey="label" width={110} {...chartAxisTickProps} />
          <Tooltip
            {...chartTooltipProps}
            formatter={((value: number, _name: unknown, item: { payload?: ToolComparisonPoint }) =>
              valueFormatter && item.payload ? valueFormatter(value, item.payload) : value.toLocaleString()) as never}
          />
          <Bar dataKey={dataKey} name={title}>
            {data.map((entry) => (
              <Cell key={entry.tool} fill={TOOL_COLORS[entry.tool]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
